import { DragDropModule } from "@angular/cdk/drag-drop";
import { CommonModule } from "@angular/common";
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from "@angular/core";
import { getApp, initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { browserLocalPersistence, getAuth, initializeAuth, inMemoryPersistence, provideAuth } from "@angular/fire/auth";
import { getDatabase, provideDatabase } from "@angular/fire/database";
import { getFunctions, provideFunctions } from "@angular/fire/functions";
import { initializeFirestore, memoryLocalCache, provideFirestore } from "@angular/fire/firestore";
import { getStorage, provideStorage } from "@angular/fire/storage";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouteReuseStrategy } from "@angular/router";
import { ServiceWorkerModule } from "@angular/service-worker";
import { IonicModule, IonicRouteStrategy } from "@ionic/angular";
import { StoreRouterConnectingModule, routerReducer } from "@ngrx/router-store";
import { StoreModule } from "@ngrx/store";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { Capacitor } from "@capacitor/core";
import { IonicStorageModule } from "@ionic/storage-angular";
import { environment } from "../environments/environment";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { enterAnimation } from "./shared/animations/routing-animation";
import { OffsetTopDirective } from "./shared/directives/offset-top";
import { ScrollableDirective } from "./shared/directives/scrollable";
import { MaterialModule } from "./shared/global-modules/material-modules";
import {
	AuthGuard,
	EventBlockedGuard,
	EventGuard,
	EventsListGuard,
	LoginGuard,
	RegisterFormGuard,
	ShortcodeGuard
} from "./shared/guards";
import { PathComponents } from "./shared/paths/path-components";
import { PipesModule } from "./shared/pipes/pipes.modules";
import {
	chatsReducers,
	containersReducers,
	eventsReducers,
	genericsModulesDataReducers,
	modulesReducers,
	utilityReducers
} from "./shared/reducers";
import { authReducers } from "./shared/reducers/auth.reducers";
import {
	AnalyticsService,
	AppointmentsService,
	AuthService,
	CardExchangeService,
	ChatsService,
	CheckinsService,
	CustomFieldsService,
	CustomPagesService,
	DeviceInfoService,
	DocumentsService,
	EventUsersService,
	EventsService,
	ExternalInteractivityService,
	FeedNewsService,
	FeedbacksService,
	FormsService,
	InteractivityService,
	LanguagesService,
	LastAccessService,
	LocationsService,
	LuxonService,
	ModulesManagerService,
	MongodbService,
	PointsService,
	QuizsService,
	RankingsService,
	ReportsService,
	ResultsService,
	SchedulesService,
	SheetsService,
	StateManagerService,
	StorageService,
	SurveysService,
	TracksService,
	TreasureHuntsService,
	UtilityService,
	VisiosService,
	WordCloudsService
} from "./shared/services";
import { NgChartsModule } from "ng2-charts";
import { GenericComponentsModule } from "./shared/generic-components/generic-components.module";
import { ValdemortModule } from "ngx-valdemort";
import { NgOtpInputModule } from "ng-otp-input";
import { LogLevel, setLogLevel } from "@angular/fire";
setLogLevel(LogLevel.SILENT);

// translate module config
export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
	return new TranslateHttpLoader(http, "../assets/i18n/", ".json");
}

// import { SafeArea } from "@capacitor-community/safe-area";
// SafeArea.enable({
// 	config: {
// 		customColorsForSystemBars: true,
// 		statusBarColor: "#00000000", // transparent
// 		statusBarContent: "light",
// 		navigationBarColor: "#00000000", // transparent
// 		navigationBarContent: "light"
// 	}
// });

@NgModule({
	declarations: [
		AppComponent,
		PathComponents.login,
		PathComponents.resetPassword,
		PathComponents.eventsList,
		PathComponents.privacy,
		PathComponents.splash,
		PathComponents.termsOfUse,
		ScrollableDirective,
		OffsetTopDirective
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [
		BrowserModule,
		CommonModule,
		IonicModule.forRoot({
			navAnimation: enterAnimation,
			swipeBackEnabled: false
		}),
		AppRoutingModule,
		NgOtpInputModule,
		BrowserAnimationsModule,
		FormsModule,
		ReactiveFormsModule,
		MaterialModule,
		IonicStorageModule.forRoot(),
		StoreModule.forRoot({
			auth: authReducers,
			chats: chatsReducers,
			containers: containersReducers,
			events: eventsReducers,
			genericsModulesData: genericsModulesDataReducers,
			modules: modulesReducers,
			router: routerReducer,
			utility: utilityReducers
		}),
		StoreRouterConnectingModule.forRoot(),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: createTranslateLoader,
				deps: [HttpClient]
			}
		}),
		PipesModule,
		ServiceWorkerModule.register("service-worker.js", {
			enabled: environment.production
		}),
		DragDropModule,
		NgChartsModule,
		GenericComponentsModule,
		ValdemortModule
	],
	providers: [
		provideFirebaseApp(() => {
			return initializeApp(environment.firebase);
		}),
		provideAuth(() => {
			if (Capacitor.isNativePlatform()) {
				// Pour iOS, utiliser browserLocalPersistence qui est plus compatible
				// avec un fallback vers inMemoryPersistence en cas d'échec
				try {
					return initializeAuth(getApp(), {
						persistence: [browserLocalPersistence, inMemoryPersistence]
					});
				} catch (error) {
					return initializeAuth(getApp(), {
						persistence: inMemoryPersistence
					});
				}
			} else {
				return getAuth();
			}
		}),
		provideFirestore(() => {
			const isNative = Capacitor.isNativePlatform();

			if (isNative) {
				return initializeFirestore(getApp(), {
					localCache: memoryLocalCache()
				});
			} else {
				return initializeFirestore(getApp(), {});
			}
		}),
		provideDatabase(() => getDatabase()),
		provideStorage(() => getStorage()),
		provideFunctions(() => {
			const app = initializeApp(environment.firebase);
			return getFunctions(app, environment.platform.region);
		}),
		// { provide: ErrorHandler, useClass: CustomErrorHandler },
		{ provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
		AuthGuard,
		EventBlockedGuard,
		EventGuard,
		RegisterFormGuard,
		EventsListGuard,
		LoginGuard,
		ShortcodeGuard,
		AnalyticsService,

		AppointmentsService,
		AuthService,
		CardExchangeService,
		ChatsService,
		CheckinsService,
		CustomFieldsService,
		CustomPagesService,
		DeviceInfoService,
		DocumentsService,
		EventsService,
		EventUsersService,
		ExternalInteractivityService,
		FeedbacksService,
		FormsService,
		InteractivityService,
		LanguagesService,
		LastAccessService,
		LocationsService,
		LuxonService,
		ModulesManagerService,
		MongodbService,
		FeedNewsService,
		PointsService,
		QuizsService,
		RankingsService,
		ReportsService,
		ResultsService,
		SchedulesService,
		SheetsService,
		StateManagerService,
		StorageService,
		SurveysService,
		TracksService,
		TreasureHuntsService,
		UtilityService,
		VisiosService,
		WordCloudsService,
		provideHttpClient(withInterceptorsFromDi())
	]
})
export class AppModule {}
